import React from "react";
import Card from "@mui/material/Card";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useAttachmentIdFromRoute, useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import { useGameRules, useUpdateRules } from "@seabrookstudios/pitch2table";
import { ImageOrPdfViewer } from "../core/ImageOrPdfViewer";
import { EditableCardHeader } from "../games/EditableHeading";
import IconButton from "@mui/material/IconButton";
import Delete from "@mui/icons-material/Delete";
import CardActions from "@mui/material/CardActions";
import CircularProgress from "@mui/material/CircularProgress";
import { CircularProgressSize } from "../theme";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CardHeader from "@mui/material/CardHeader";
import { assetPath } from "../endpoints";
import Download from "@mui/icons-material/Download";
import { enqueueSnackbar } from "notistack";

export const RulesFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();
  const updateRules = useUpdateRules(gameId, attachmentId);
  const { data: rules = [] } = useGameRules(gameId);

  if (!attachmentId) {
    return <EmptyDetailCard text="Select a rules version from the list on the left." />;
  }

  /**
   * @param {string} name
   */
  const updateRulesName = async (name) => {
    await updateRules.mutateAsync({ deleted: undefined, name });
  };

  const recoverRules = async () => {
    await updateRules.mutateAsync({ deleted: false, name: undefined });
  };

  const deleteRules = async () => {
    await updateRules.mutateAsync({ deleted: true, name: undefined });
  };

  const doc = rules.find((doc) => doc.id === attachmentId);

  if (!doc) {
    return <CircularProgress />;
  }

  const handleDownload = async () => {
    try {
      const response = await fetch(assetPath(doc.s3location));

      const blob = await response.blob();

      const internalUrl = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = internalUrl;
      link.download = `${doc.name || "Rules"}.pdf`;
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(internalUrl);
    } catch (error) {
      enqueueSnackbar("Download failed", { variant: "error" });
    }
  };

  const Icon = doc.deleted ? RestoreFromTrash : Delete;

  return (
    <Card elevation={1} square={false}>
      <Stack direction="row" justifyContent="space-between">
        <EditableCardHeader
          title="Document Name"
          subheader={doc.name ? "" : "Document Name"}
          text={doc.name || ""}
          onSave={updateRulesName}
          helperText="A name for this rules version e.g. Solo Rules v3"
        />
        <CardHeader
          action={
            <IconButton onClick={handleDownload}>
              <Download />
            </IconButton>
          }
        />
      </Stack>
      {doc.deleted ? (
        <Box p={2}>
          <Alert severity="warning">This file has been deleted. You can restore it using the button below.</Alert>
        </Box>
      ) : (
        <ImageOrPdfViewer document={doc} />
      )}
      <CardActions>
        <IconButton onClick={doc.deleted ? recoverRules : deleteRules}>
          {updateRules.isPending ? <CircularProgress size={CircularProgressSize} /> : <Icon />}
        </IconButton>
      </CardActions>
    </Card>
  );
};
